import React, { useState } from "react";
import { X, AlertCircle, Loader2 } from "lucide-react";

const RejectRequestModal = ({
  isOpen,
  product,
  onClose,
  onConfirm,
  isRejecting
}) => {
  const [rejectionReason, setRejectionReason] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    if (rejectionReason.trim()) {
      onConfirm(rejectionReason.trim());
    }
  };

  const handleClose = () => {
    setRejectionReason("");
    onClose();
  };

  if (!isOpen || !product) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
      onClick={handleClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b bg-red-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
              <AlertCircle size={20} className="text-red-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-red-900">
                Reject Product Request
              </h2>
              <p className="text-xs text-red-700">
                Please provide a reason
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={isRejecting}
            className="p-2 hover:bg-red-100 rounded-lg transition disabled:opacity-50"
          >
            <X size={20} className="text-red-900" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Product Info */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              {product.productImg ? (
                <img
                  src={product.productImg}
                  alt={product.productName}
                  className="w-12 h-12 rounded-lg object-cover"
                />
              ) : (
                <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center">
                  <AlertCircle size={20} className="text-gray-400" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm text-gray-900 truncate">
                  {product.productName}
                </p>
                <p className="text-xs text-gray-500">
                  {product.brand} • Requested: {product.requestedQuantity || 0}
                </p>
              </div>
            </div>
          </div>

          {/* Rejection Reason Input */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Reason for Rejection <span className="text-red-500">*</span>
            </label>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="e.g., Product out of stock, Not suitable for this service..."
              rows={4}
              disabled={isRejecting}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 resize-none disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
            <p className="text-xs text-gray-500 mt-1">
              Staff member will see this reason
            </p>
          </div>

          {/* Warning Message */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex gap-2">
            <AlertCircle size={16} className="text-yellow-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-yellow-800">
              This action cannot be undone. The staff member will be notified of the rejection.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={handleClose}
              disabled={isRejecting}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isRejecting || !rejectionReason.trim()}
              className="flex-1 px-6 py-3 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isRejecting ? (
                <>
                  <Loader2 className="animate-spin" size={18} />
                  Rejecting...
                </>
              ) : (
                "Reject Request"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RejectRequestModal;